/**
 * Runtime environment configuration
 *
 * This module provides a unified interface for accessing configuration
 * from either:
 * 1. Build-time Vite env vars (import.meta.env) - used in local dev
 * 2. Runtime config (window._env_) - used in Docker deployments
 *
 * The runtime config is injected by docker-entrypoint.sh at container
 * startup, allowing the same Docker image to work across all environments.
 */

declare global {
  interface Window {
    _env_?: {
      VITE_GATEWAY_URL?: string;
      VITE_OKTA_ISSUER?: string;
      VITE_OKTA_CLIENT_ID?: string;
      VITE_APP_NAME?: string;
    };
  }
}

/**
 * Get configuration value with runtime override support
 * Priority: window._env_ (runtime) > import.meta.env (build-time)
 */
function getEnv(key: string, defaultValue: string = ''): string {
  // Check runtime config first (Docker deployments)
  if (typeof window !== 'undefined' && window._env_) {
    const value = window._env_[key as keyof typeof window._env_];
    if (value) {
      return value;
    }
  }

  // Fallback to build-time config (local development)
  const buildTimeValue = import.meta.env[key];
  return buildTimeValue || defaultValue;
}

export const config = {
  gatewayUrl: getEnv('VITE_GATEWAY_URL', 'http://localhost:8000'),
  oktaIssuer: getEnv('VITE_OKTA_ISSUER', ''),
  oktaClientId: getEnv('VITE_OKTA_CLIENT_ID', ''),
  appName: getEnv('VITE_APP_NAME', 'Okta MCP Adapter Admin'),
};

// Validate required config
if (!config.oktaIssuer || !config.oktaClientId) {
  console.warn(
    'Admin UI configuration incomplete. Okta authentication will not work.',
    {
      oktaIssuer: config.oktaIssuer ? '✓ set' : '✗ missing',
      oktaClientId: config.oktaClientId ? '✓ set' : '✗ missing'
    }
  );
}
